import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import InputText from "../../../../components/form-control/input-text";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from 'primereact/card';
import { Skeleton } from "primereact/skeleton";
import { Select, Spin } from "antd";
import useToast from "../../../../components/common/useToast";
import { useDispatch } from "react-redux";
import apiFunctions from "../../../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";
import { persistor } from "../../../../redux/store";
import { logout } from "../../../../redux/features/authSlice";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const { Option } = Select;

const SurnameCreate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();
    const [data, setData] = useState<any[]>([]);
    const [first, setFirst] = useState(0);
    const [rows] = useState(10);
    const [searchValue, setSearchValue] = useState('');
    const [fetching, setFetching] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(!!id);
    const dispatch = useDispatch();
    const selectRef = useRef<any>(null);

    const validationSchema = Yup.object().shape({
        name: Yup.string().max(30).required("Name is required!"),
        namegujarati: Yup.string().required("Gujarati Name is required!"),
        subcastid: Yup.string().required("Country is required!"),
    });

    const initialValue = {
        name: "",
        namegujarati: "",
        subcastid: ""
    };

    const formik = useFormik({
        initialValues: initialValue,
        validationSchema: validationSchema,

        onSubmit: async (values: any) => {
            let payload = {
                name: values.name,
                namegujarati: values.namegujarati,
                subcastid: values.subcastid,
            }
            if (id) {
                await apiFunctions.put(`/surname/editsurname/${id}`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    })
                    .finally(() => {
                        navigate("/admin/surnames");
                    });
            } else {
                await apiFunctions.post(`/surname/addsurname`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    })
                    .finally(() => {
                        navigate("/admin/surnames");
                    });
            }
        },
    });

    const handleBack = () => {
        navigate('/admin/surnames');
    }

    const getSurnameData = async (id: string) => {
        try {
            let url = `/surname/${id}`;
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        formik.setFieldValue("name", response.data.data.name);
                        formik.setFieldValue("namegujarati", response.data.data.namegujarati);
                        formik.setFieldValue("subcastid", response.data.data.subcastid);
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    const convertguharaticall = async (value: string) => {
        try {
            if (value.length > 0) {
                let url = `/textmobile/mobile/translate/${value}`;
                await apiFunctions
                    .get(url)
                    .then((response) => {
                        console.log(response, "response");
                        if (response && response.data && response.data.success) {
                            formik.setFieldValue("namegujarati", response.data.data);
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        errorHandler(err);
                    })
                    .finally(() => {
                        // setLoad(false);
                    });
            } else {
                formik.setFieldValue("namegujarati", "");
            }
        } catch (error) {
            console.log(error);
        }
    }

    const handleNameChange = (value: string) => {
        formik.setFieldValue("name", value);
        if (value.length > 0) {
            convertguharaticall(value);
        } else {
            formik.setFieldValue("namegujarati", "");
        }
    }

    const fetchSubcasts = async (reset = false) => {
        if (fetching) return;
        setFetching(true);
        const start = reset ? 0 : first;
        try {
            const response = await apiFunctions.get(
                `/subcast/subcastdropdownlist/${start}/${rows}/${searchValue || ''}`
            );

            const newItems = response?.data?.data || [];
            const total = response?.data?.totalRecords || 0;

            if (reset) {
                setData(newItems);
                setFirst(rows);
            } else {
                setData(prev => [...prev, ...newItems]);
                setFirst(prev => prev + rows);
            }

            setTotalRecords(total);
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        setFirst(0);
        fetchSubcasts(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    const handleScroll = (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;

        const nearBottom = scrollTop + offsetHeight >= scrollHeight - 10;
        const moreToLoad = data.length < totalRecords;

        if (nearBottom && moreToLoad && !fetching) {
            fetchSubcasts();
        }
    };

    const handleSearch = (value: any) => {
        setSearchValue(value);
    };

    useEffect(() => {
        if (selectRef.current) {
            selectRef.current.focus();
        }
        if (id) {
            getSurnameData(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                {id ? (<h3>Edit Surname</h3>) : (<h3>Add Surname</h3>)}
            </div>
            <form action="submit" onSubmit={formik.handleSubmit}>
                <Card className="grid form-layout">
                    {loading ? (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="60px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="80px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-2">
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="120px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    <Skeleton width="90px" height="40px" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <div className="field">
                                        <label htmlFor={`countryname`} className={`form-label`}>
                                            Subcast
                                        </label>
                                        <Select
                                            ref={selectRef}
                                            allowClear
                                            onClear={() => formik.setFieldValue("countryname", "")}
                                            showSearch
                                            style={{ width: "100%", height: "42px" }}
                                            status={
                                                formik.touched.subcastid && formik.errors.subcastid
                                                    ? "error"
                                                    : ""
                                            }
                                            placeholder=""
                                            filterOption={false}
                                            onSearch={handleSearch}
                                            onPopupScroll={handleScroll}
                                            notFoundContent={fetching ? <Spin size="small" /> : 'No data'}
                                            value={formik.values.subcastid} // bind Formik value
                                            onChange={(value) => {
                                                formik.setFieldValue("subcastid", value);
                                            }}
                                        >
                                            {data.map(subcast => (
                                                <Option key={subcast.id} value={subcast.id}>
                                                    {subcast.name}
                                                </Option>
                                            ))}
                                        </Select>
                                        {formik.touched.subcastid && typeof formik.errors.subcastid === "string" && (
                                            <span id="subcastid-help" className="p-error text-left mt-2">
                                                {formik.errors.subcastid}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <InputText
                                        type="text"
                                        label="Name"
                                        placeholder=""
                                        modelValue={formik.values.name}
                                        disabled={false}
                                        autofocus={false}
                                        id="name"
                                        validation={
                                            formik.errors.name &&
                                            formik.touched.name && {
                                                $error: true,
                                                required: {
                                                    $invalid: true,
                                                    $message: formik.errors.name,
                                                },
                                            }
                                        }
                                        onUpdateModelValue={handleNameChange}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-2">
                                <div className="col-span-12 md:col-span-6">
                                    <InputText
                                        type="text"
                                        label="Gujarati Name"
                                        placeholder=""
                                        modelValue={formik.values.namegujarati}
                                        disabled={false}
                                        autofocus={false}
                                        id="namegujarati"
                                        validation={
                                            formik.errors.namegujarati &&
                                            formik.touched.namegujarati && {
                                                $error: true,
                                                required: {
                                                    $invalid: true,
                                                    $message: formik.errors.namegujarati,
                                                },
                                            }
                                        }
                                        onUpdateModelValue={function (value: string): void {
                                            formik.setFieldValue("namegujarati", value);
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="field mt-5 mb-4">
                                    {id ? (
                                        <Button
                                            label="Edit"
                                            className="prime-button btn-login w-max"
                                            type="submit"
                                        ></Button>
                                    ) : (
                                        <Button
                                            label="ADD"
                                            className="prime-button btn-login w-max"
                                            type="submit"
                                        ></Button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Card>
            </form >
        </>
    );
};

export default SurnameCreate;
